import { Helmet } from "react-helmet-async";

const SITE_NAME = "Sift Careers";
const SITE_URL = "https://www.siftcareers.com";
const DEFAULT_DESCRIPTION =
  "All-in-one contractor management platform. Contracts, timecards, payments, credentials, and compliance in one place. Replace staffing agency markups with transparent pricing.";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * SEO head tag manager. Use on every public page.
 *
 * @param {string} title - Page title (appended with " | Sift Careers")
 * @param {string} description - Meta description (max ~155 chars)
 * @param {string} path - URL path (e.g. "/about")
 * @param {string} image - OG image URL
 * @param {string} type - OG type (default "website", use "article" for blog)
 * @param {object} schema - JSON-LD structured data object(s)
 * @param {string} publishedDate - ISO date for articles
 * @param {string[]} keywords - SEO keywords
 * @param {boolean} noIndex - Set true for pages that shouldn't be indexed
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  schema,
  publishedDate,
  keywords,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = `${SITE_URL}${path}`;

  // Base organization schema always present
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Sift Careers",
    url: SITE_URL,
    logo: `${SITE_URL}/sift-app-icon.png`,
    description: DEFAULT_DESCRIPTION,
    foundingDate: "2024",
    founder: {
      "@type": "Person",
      name: "Jake Sutton",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Carmel",
      addressRegion: "IN",
      addressCountry: "US",
    },
    sameAs: [
      "https://www.linkedin.com/company/siftcareers",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      email: "jsutton@siftcareers.com",
      contactType: "sales",
    },
  };

  // SoftwareApplication schema for the product
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Sift Careers",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: "Contractor management platform for contracts, timecards, payments, credentials, and compliance.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Service fee on payments processed. No subscription required to start.",
    },
    featureList: [
      "Contract generation and e-signatures",
      "Timecard submission and approval",
      "Stripe-powered contractor payments",
      "Credential collection and tracking",
      "White-label partner portals",
      "Purchase order budget controls",
      "AI-powered contractor matching",
      "SOC 2 ready security controls",
    ],
  };

  const schemas = [orgSchema];
  if (path === "" || path === "/") schemas.push(softwareSchema);
  if (schema) {
    if (Array.isArray(schema)) schemas.push(...schema);
    else schemas.push(schema);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      {publishedDate && <meta property="article:published_time" content={publishedDate} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Generate BlogPosting JSON-LD schema
 */
export function blogPostSchema({ title, description, slug, publishedDate, readTime, keywords, excerpt }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: description || excerpt,
    url: `https://www.siftcareers.com/blog/${slug}`,
    datePublished: publishedDate,
    dateModified: publishedDate,
    author: {
      "@type": "Person",
      name: "Jake Sutton",
      url: "https://www.siftcareers.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Sift Careers",
      logo: {
        "@type": "ImageObject",
        url: "https://www.siftcareers.com/sift-app-icon.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.siftcareers.com/blog/${slug}`,
    },
    keywords: keywords?.join(", ") || "",
    timeRequired: readTime ? `PT${parseInt(readTime)}M` : undefined,
    inLanguage: "en-US",
  };
}

/**
 * Generate FAQPage JSON-LD schema
 */
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };
}

/**
 * Generate BreadcrumbList JSON-LD schema
 */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `https://www.siftcareers.com${item.path}`,
    })),
  };
}
